import { API_BASE_URL, handleAuthFailure } from '@/services/apiConfig'

export interface ChatStreamBody {
  question: string
  session_id?: string
  folder_id?: number
  file_id?: number
  conversation_history?: Array<{ type: 'user' | 'assistant'; content: string }>
}

/** A non-2xx response before the stream opened (422, 429, 500...), or a dropped/stalled stream (status 0). */
export class ChatStreamError extends Error {
  constructor(
    public status: number,
    public data: any
  ) {
    super(data?.error || data?.message || `Chat stream failed (${status})`)
    this.name = 'ChatStreamError'
  }
}

/** Give up if the server goes quiet for this long mid-answer. */
const IDLE_TIMEOUT_MS = 60_000

const FRAME_SEPARATOR = /\r?\n\r?\n/

/**
 * POST /chat/stream and hand each server-sent event to `onEvent` as it arrives.
 * Components go through apiService.streamChat(); this is its implementation.
 *
 * Uses fetch instead of axios/EventSource because we need a POST body and the
 * auth header. Resolves when the stream ends; rejects with a ChatStreamError for
 * HTTP errors or a stalled stream, or with an AbortError when `signal` aborts.
 */
export async function streamChat(
  body: ChatStreamBody,
  onEvent: (event: string, data: any) => void,
  signal: AbortSignal
): Promise<void> {
  const controller = new AbortController()
  const forwardAbort = () => controller.abort(signal.reason)
  signal.addEventListener('abort', forwardAbort)

  let idleTimer: ReturnType<typeof setTimeout> | undefined
  let timedOut = false
  const resetIdleTimer = () => {
    clearTimeout(idleTimer)
    idleTimer = setTimeout(() => {
      timedOut = true
      controller.abort()
    }, IDLE_TIMEOUT_MS)
  }

  try {
    resetIdleTimer()
    const token = localStorage.getItem('auth_token')
    const response = await fetch(`${API_BASE_URL}/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // JSON first so Laravel answers auth/validation failures with JSON (401/422)
        // instead of trying to redirect to a login page and failing with a 500
        Accept: 'application/json, text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    })

    if (!response.ok || !response.body) {
      const data = await response.json().catch(() => null)
      handleAuthFailure(response.status, data)
      throw new ChatStreamError(response.status, data)
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    for (;;) {
      const { value, done } = await reader.read()
      if (done) break

      resetIdleTimer()
      buffer += decoder.decode(value, { stream: true })

      let match: RegExpExecArray | null
      while ((match = FRAME_SEPARATOR.exec(buffer)) !== null) {
        const frame = buffer.slice(0, match.index)
        buffer = buffer.slice(match.index + match[0].length)
        dispatchFrame(frame, onEvent)
      }
    }

    if (buffer.trim() !== '') {
      dispatchFrame(buffer, onEvent)
    }
  } catch (err) {
    if (timedOut) {
      throw new ChatStreamError(0, { error: 'stream_timeout' })
    }
    if (err instanceof TypeError) {
      // fetch/network failure, e.g. connection dropped mid-answer
      throw new ChatStreamError(0, { error: 'network_error' })
    }
    throw err
  } finally {
    clearTimeout(idleTimer)
    signal.removeEventListener('abort', forwardAbort)
  }
}

function dispatchFrame(frame: string, onEvent: (event: string, data: any) => void): void {
  let event = 'message'
  const dataLines: string[] = []

  for (const line of frame.split(/\r?\n/)) {
    if (line.startsWith('event:')) {
      event = line.slice(6).trim()
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).replace(/^ /, ''))
    }
  }

  if (dataLines.length === 0) return

  try {
    onEvent(event, JSON.parse(dataLines.join('\n')))
  } catch {
    // Ignore a malformed frame rather than killing the whole answer
  }
}
